// SRS 復習。期限の来た保存単語を1枚ずつ出し、4段階で採点（ts-fsrs）。
// 採点結果は vocab に保存され、同期で PC と双方向に揃う。
import { dueCards, reviewCard, nextDue } from '../lib/vocab.js'
import { speak, ttsAvailable } from '../lib/tts.js'
import { bgSync } from '../lib/sync.js'
import { isConnected as gdriveConnected } from '../lib/gdrive.js'
import { icon } from '../lib/icons.js'
import { t } from '../lib/i18n.js'

function escapeHtml(s) {
  return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
}

// ISO の未来時刻 → 「10分後」等の相対表記。
function fmtUntil(iso) {
  const ms = new Date(iso).getTime() - Date.now()
  if (!Number.isFinite(ms)) return ''
  const m = Math.round(ms / 60000)
  if (m < 60) return t('time.min', { n: Math.max(1, m) })
  const h = Math.round(m / 60)
  if (h < 24) return t('time.hour', { n: h })
  const d = Math.round(h / 24)
  if (d < 30) return t('time.day', { n: d })
  return t('time.month', { n: Math.round(d / 30) })
}

function senseHtml(senses) {
  return (senses || [])
    .slice(0, 8)
    .map((e) => {
      const m = e.match(/^(\{[^}]*\})\s*(.*)$/)
      return m
        ? `<div class="sense"><span class="pos">${escapeHtml(m[1])}</span>${escapeHtml(m[2])}</div>`
        : `<div class="sense">${escapeHtml(e)}</div>`
    })
    .join('')
}

export async function renderReview(root, flash = '') {
  let i = 0

  // 開いた時に裏で自動 pull（連携時のみ）。新しい復習対象が増え、まだ採点していなければ取り直す。
  async function maybeAutoSync() {
    if (!gdriveConnected()) return
    const r = await bgSync()
    if (r && r.ok && r.vocab && (r.vocab.added > 0 || r.vocab.merged > 0) && i === 0) {
      renderReview(root)
    }
  }

  // 手動更新：「同期中…」を出して今すぐ同期 → 件数を添えて復習画面を取り直す。
  function wireRefresh() {
    const btn = root.querySelector('#rv-refresh')
    if (!btn) return
    btn.onclick = async () => {
      btn.disabled = true
      btn.textContent = t('sync.syncing')
      const r = await bgSync()
      const n = r && r.vocab ? r.vocab.added : 0
      const msg = r && r.skipped ? '' : n > 0 ? t('sync.got', { n }) : t('sync.upToDate')
      renderReview(root, msg)
    }
  }

  const flashHtml = flash
    ? `<div class="muted" id="rv-sync" style="font-size:12px;margin-bottom:6px">${escapeHtml(flash)}</div>`
    : ''

  const cards = await dueCards()
  if (!cards.length) {
    const nd = await nextDue()
    root.innerHTML = `
      <div class="head-row"><h1>${t('review.title')}</h1><button class="mini" id="rv-refresh">${t('sync.refresh')}</button></div>
      ${flashHtml}
      <div class="card"><p class="muted">${t('review.noneNow')}</p>
      ${nd ? `<p class="muted">${t('review.nextIn', { t: fmtUntil(nd) })}</p>` : `<p class="muted">${t('review.saveToReview')}</p>`}</div>`
    wireRefresh()
    maybeAutoSync()
    return
  }

  const ttsBtn = ttsAvailable() ? `<button class="mini secondary" id="tts" aria-label="${t('common.speak')}">${icon('speaker')}</button>` : ''

  function showCard() {
    if (i >= cards.length) {
      bgSync() // 採点結果(SRS)を裏でクラウドへ反映（PCと双方向）
      root.innerHTML = `<h1>${t('review.title')}</h1>
        <div class="card"><p>${t('review.done', { n: cards.length })}</p>
        <button class="btn" id="again">${t('review.again')}</button></div>`
      root.querySelector('#again').onclick = () => renderReview(root)
      return
    }
    const c = cards[i]
    // 更新ボタンは開始時(未採点)だけ出す（採点途中は取り直しで進捗が飛ぶため出さない）。
    const refreshBtn = i === 0 ? `<button class="mini" id="rv-refresh">${t('sync.refresh')}</button>` : ''
    root.innerHTML = `
      <div class="head-row"><h1>${t('review.title')}</h1>
        <div style="display:flex;gap:8px;align-items:center">
          <span class="muted">${t('review.progress', { i: i + 1, n: cards.length })}</span>${refreshBtn}
        </div></div>
      ${i === 0 ? flashHtml : ''}
      <div class="card review-card">
        <div class="rv-word">${escapeHtml(c.word)} ${ttsBtn}</div>
        <div class="rv-context muted">${c.context ? escapeHtml(c.context) : ''}</div>
        <div class="rv-back" id="back" hidden>${senseHtml(c.senses)}</div>
        <button class="btn" id="reveal">${t('review.reveal')}</button>
        <div class="rv-grades" id="grades" hidden>
          <button class="grade again" data-r="1">${t('review.grade.again')}<small>${fmtUntil(c.preview.again)}</small></button>
          <button class="grade hard" data-r="2">${t('review.grade.hard')}<small>${fmtUntil(c.preview.hard)}</small></button>
          <button class="grade good" data-r="3">${t('review.grade.good')}<small>${fmtUntil(c.preview.good)}</small></button>
          <button class="grade easy" data-r="4">${t('review.grade.easy')}<small>${fmtUntil(c.preview.easy)}</small></button>
        </div>
      </div>`

    wireRefresh()
    const ttsEl = root.querySelector('#tts')
    if (ttsEl) ttsEl.onclick = () => speak(c.word)
    root.querySelector('#reveal').onclick = () => {
      root.querySelector('#back').hidden = false
      root.querySelector('#reveal').hidden = true
      root.querySelector('#grades').hidden = false
    }
    root.querySelector('#grades').onclick = async (e) => {
      const b = e.target.closest('button[data-r]')
      if (!b) return
      await reviewCard(c.key, Number(b.dataset.r))
      i++
      showCard()
    }
  }
  showCard()
  maybeAutoSync() // 初回描画後に裏で同期（待たない）
}
